import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, ColorPicker, Form, Select, Space, Typography } from "antd";
import type { Color } from "antd/es/color-picker";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { ClientSettingAuditRow } from "../../../model/client/ClientSettingAuditRow";

const { Text } = Typography;

// Strict 6-digit hex. Matches the regex on the API so the operator sees the
// same validation feedback the server would enforce.
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

// Audit keys that together make a complete theme snapshot. Same constants as
// the backend; duplicated here because importing from the API project would
// cross the package boundary.
const THEME_KEYS = {
    primaryColor: 'branding.primaryColor',
    sidebarBg: 'branding.sidebarBg',
    submenuBg: 'branding.submenuBg',
    sidebarItemSelectedBg: 'branding.sidebarItemSelectedBg',
} as const;

const REQUIRED_THEME_KEYS = Object.values(THEME_KEYS);

export interface ThemeSectionValue {
    primaryColor: string;
    sidebarBg: string;
    submenuBg: string;
    sidebarItemSelectedBg: string;
}

export interface ThemeSnapshot {
    // Stable id derived from the audit row's changedAt timestamp (numeric
    // epoch ms) so list keys stay primitive and identity-stable.
    snapshotId: string;
    changedAt: Date;
    changedBy: string;
    colors: ThemeSectionValue;
}

// Fall-back palette used when /config has never been saved for this tenant.
// Mirrors the hardcoded defaults in App.tsx so an unsaved tenant renders the
// same colors as it does today before the first save.
export const DEFAULT_THEME: ThemeSectionValue = {
    primaryColor: '#00005a',
    sidebarBg: '#00005a',
    submenuBg: '#161b38',
    sidebarItemSelectedBg: '#2a305c',
};

// Group raw audit rows into complete theme snapshots. Rows whose changedAt
// matches but which don't include all four theme keys (e.g. an identity-only
// save that also touched a branding.* key) are filtered out so the dropdown
// only ever offers fully-applicable past versions. Newest-first.
//
// Keys the Map by the numeric epoch (getTime()) rather than the Date object
// itself: DateReviver hands us a fresh Date instance per JSON entry, so two
// audit rows from the same save would have distinct Date *references* but
// equal epoch ms — and Map keys use SameValueZero, which treats different
// Date instances as distinct. Using epoch ms gives a primitive key with the
// equality semantics we actually want.
export function groupThemeSnapshots(rows: ClientSettingAuditRow[]): ThemeSnapshot[] {
    const byTimestamp = new Map<number, ClientSettingAuditRow[]>();
    for (const row of rows) {
        const epoch = row.changedAt.getTime();
        const bucket = byTimestamp.get(epoch) ?? [];
        bucket.push(row);
        byTimestamp.set(epoch, bucket);
    }

    const snapshots: ThemeSnapshot[] = [];
    // Array.from keeps the file compiling under tsconfig targets that don't
    // support direct Map iteration (CRA's default).
    Array.from(byTimestamp.entries()).forEach(([epoch, group]) => {
        const byKey: Partial<Record<string, ClientSettingAuditRow>> = {};
        for (const row of group) byKey[row.key] = row;
        if (REQUIRED_THEME_KEYS.some((k) => !byKey[k])) return;
        snapshots.push({
            snapshotId: String(epoch),
            changedAt: new Date(epoch),
            // changedBy is identical across rows in a single upsert; pick the
            // first row's value rather than asserting equality.
            changedBy: group[0].changedBy,
            colors: {
                primaryColor: byKey[THEME_KEYS.primaryColor]!.value,
                sidebarBg: byKey[THEME_KEYS.sidebarBg]!.value,
                submenuBg: byKey[THEME_KEYS.submenuBg]!.value,
                sidebarItemSelectedBg: byKey[THEME_KEYS.sidebarItemSelectedBg]!.value,
            },
        });
    });

    snapshots.sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
    return snapshots;
}

interface FieldSpec {
    key: keyof ThemeSectionValue;
    label: string;
    testId: string;
}

const FIELDS: ReadonlyArray<FieldSpec> = [
    { key: 'primaryColor', label: 'Primary color', testId: 'settings-theme-primary' },
    { key: 'sidebarBg', label: 'Sidebar background', testId: 'settings-theme-sidebar-bg' },
    { key: 'submenuBg', label: 'Submenu background', testId: 'settings-theme-submenu-bg' },
    { key: 'sidebarItemSelectedBg', label: 'Selected item background', testId: 'settings-theme-selected-bg' },
];

// Controlled section component for the four branding color fields. The host
// owns the value so the same component can be mounted by both the settings
// shell and the wizard's branding step.
export const ThemeSection: React.FC<SectionPanelProps<ThemeSectionValue>> = ({
    value,
    onChange,
    readOnly,
}) => {
    const update = (key: keyof ThemeSectionValue, next: string) => {
        onChange({ ...value, [key]: next });
    };

    const previewStyles = useMemo(() => ({
        sidebar: { background: value.sidebarBg, color: '#fff', padding: 8, borderRadius: 4 },
        submenu: { background: value.submenuBg, color: '#fff', padding: 8 },
        selected: { background: value.sidebarItemSelectedBg, color: '#fff', padding: 8 },
        primaryBtn: { background: value.primaryColor, color: '#fff', border: 'none' },
    }), [value]);

    return (
        <Form layout="vertical" data-testid="settings-theme-form" disabled={readOnly}>
            {FIELDS.map(({ key, label, testId }) => {
                const current = value[key];
                const invalid = !HEX_COLOR_REGEX.test(current);
                return (
                    <Form.Item
                        key={key}
                        label={label}
                        required
                        validateStatus={invalid ? 'error' : undefined}
                        help={invalid ? 'Enter a 6-digit hex color (e.g. #00005a).' : undefined}
                    >
                        <ColorPicker
                            data-testid={testId}
                            value={current}
                            disabledAlpha
                            format="hex"
                            // Use toHexString() rather than the second `css`
                            // arg — that arg is the result of toCssString()
                            // which returns "rgb(r,g,b)" regardless of the
                            // configured format, and would fail the hex regex.
                            onChange={(color: Color) => update(key, color.toHexString().toLowerCase())}
                            showText
                        />
                    </Form.Item>
                );
            })}

            <Form.Item label="Preview">
                <Space direction="vertical" size={0} style={{ width: 240 }} data-testid="settings-theme-preview">
                    <div style={previewStyles.sidebar}>Sidebar</div>
                    <div style={previewStyles.submenu}>Submenu item</div>
                    <div style={previewStyles.selected}>Selected item</div>
                    <Space style={{ marginTop: 8 }}>
                        <Button style={previewStyles.primaryBtn} data-testid="settings-theme-preview-btn">
                            Primary action
                        </Button>
                    </Space>
                </Space>
            </Form.Item>
        </Form>
    );
};

// Renders the timestamp + actor + 4 swatches for one history option. Pulled
// out so the same renderer can be used in both the closed Select label and
// the open dropdown.
const SnapshotOption: React.FC<{ snapshot: ThemeSnapshot }> = ({ snapshot }) => {
    const swatch = (color: string): React.CSSProperties => ({
        display: 'inline-block',
        width: 14,
        height: 14,
        marginRight: 2,
        borderRadius: 2,
        background: color,
        border: '1px solid rgba(0,0,0,0.15)',
        verticalAlign: 'middle',
    });
    // Render the timestamp with the operator's local timezone. The Date is
    // already revived from the JSON response by DateReviver; if it failed to
    // parse for some reason getTime() will return NaN and we fall back to the
    // ISO string so a malformed payload still renders something readable.
    const display = Number.isNaN(snapshot.changedAt.getTime())
        ? String(snapshot.changedAt)
        : snapshot.changedAt.toLocaleString();
    return (
        <Space size="small">
            <span>{display}</span>
            <Text type="secondary">· {snapshot.changedBy}</Text>
            <span aria-hidden>
                <span style={swatch(snapshot.colors.primaryColor)} />
                <span style={swatch(snapshot.colors.sidebarBg)} />
                <span style={swatch(snapshot.colors.submenuBg)} />
                <span style={swatch(snapshot.colors.sidebarItemSelectedBg)} />
            </span>
        </Space>
    );
};

// Adapter that mounts ThemeSection inside the Settings shell: loads current
// branding colors from /config, tracks dirty state, and saves via PUT.
export const ThemeSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<ThemeSectionValue | null>(null);
    const [value, setValue] = useState<ThemeSectionValue>(DEFAULT_THEME);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState<ThemeSnapshot[]>([]);

    // Pulled into a callback so the post-save handler can reuse it without
    // duplicating the request shape. Errors are swallowed: the history
    // dropdown is a convenience, not load-blocking — a failed fetch just
    // leaves the dropdown empty rather than tripping the section's load
    // error state.
    const refreshHistory = useCallback(async () => {
        if (!Number.isFinite(clientId)) {
            setHistory([]);
            return;
        }
        try {
            const rows = await service.getSettingsHistory(clientId, {
                keyPrefix: 'branding.',
                limit: 50,
            });
            setHistory(groupThemeSnapshots(rows));
        } catch {
            setHistory([]);
        }
    }, [service, clientId]);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            // Surface the bad URL rather than rendering pickers populated with
            // DEFAULT_THEME — the operator might "restore" defaults against
            // the wrong (or no) tenant without realising the form was broken.
            setLoadError('Invalid client id in URL.');
            return;
        }
        // Clear any prior "invalid id" error so navigating from a bad URL to a
        // good one re-renders the form rather than sticking on the alert.
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                const branding = config?.settings.branding;
                // Substitute the same defaults App.tsx uses when a color is
                // unset, so the pickers don't open on '#000000' (which would
                // misleadingly imply the tenant has black as their theme).
                const loaded: ThemeSectionValue = {
                    primaryColor: branding?.primaryColor || DEFAULT_THEME.primaryColor,
                    sidebarBg: branding?.sidebarBg || DEFAULT_THEME.sidebarBg,
                    submenuBg: branding?.submenuBg || DEFAULT_THEME.submenuBg,
                    sidebarItemSelectedBg:
                        branding?.sidebarItemSelectedBg || DEFAULT_THEME.sidebarItemSelectedBg,
                };
                setInitial(loaded);
                setValue(loaded);
                await refreshHistory();
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load settings.');
            }
        })();
        return () => { cancelled = true; };
    }, [service, clientId, refreshHistory]);

    const allValid = (Object.keys(value) as Array<keyof ThemeSectionValue>)
        .every((k) => HEX_COLOR_REGEX.test(value[k]));

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);

    const handleSave = async () => {
        if (!Number.isFinite(clientId)) return;
        setSaving(true);
        setSaveError(null);
        try {
            await service.updateTheme(clientId, {
                primaryColor: value.primaryColor,
                sidebarBg: value.sidebarBg,
                submenuBg: value.submenuBg,
                sidebarItemSelectedBg: value.sidebarItemSelectedBg,
            });
            setInitial(value);
            // Refresh so the just-saved version appears at the top of the
            // restore dropdown immediately.
            await refreshHistory();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleRestore = (snapshotId: string) => {
        const snapshot = history.find((s) => s.snapshotId === snapshotId);
        if (!snapshot) return;
        // Populate the form only; the operator still has to hit Save to commit,
        // matching the live-preview-then-save flow they get from manual edits.
        setValue(snapshot.colors);
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-theme-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {history.length > 0 && (
                <Form layout="vertical">
                    <Form.Item label="Restore previous version">
                        <Select
                            data-testid="settings-theme-history-select"
                            placeholder="Pick a previous version to preview"
                            allowClear
                            style={{ width: '100%', maxWidth: 480 }}
                            onChange={(val) => val && handleRestore(val)}
                            options={history.map((snapshot) => ({
                                value: snapshot.snapshotId,
                                label: <SnapshotOption snapshot={snapshot} />,
                            }))}
                        />
                    </Form.Item>
                </Form>
            )}
            <ThemeSection value={value} onChange={setValue} />
            {saveError && (
                <Alert type="error" message={saveError} data-testid="settings-theme-save-error" />
            )}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-theme-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !allValid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-theme-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
